import { SupabaseClient } from '@supabase/supabase-js';
import { supabaseClient } from './supabaseClient';
import { UserProfile, MultiplayerRoom, GameSession, GameCartridge } from '../types';

class DatabaseService {
    private supabase: SupabaseClient;

    constructor() {
        this.supabase = supabaseClient;
  }

  // ===== Global/Realm Chat (best-effort persistence) =====
  async saveWorldChatMessage(message: any): Promise<void> {
    try {
      const { error } = await this.supabase
        .from('world_chat')
        .insert({
          user_id: message.userId || null,
          user_name: message.userName || message.sender,
          text: message.text,
          created_at: new Date(message.timestamp || Date.now()).toISOString()
        });
      if (error) throw error;
    } catch (e) {
      console.warn('[DB] saveWorldChatMessage skipped:', e);
    }
  }

  async saveRealmChatMessage(realmId: string, message: any): Promise<void> {
    try {
      const { error } = await this.supabase
        .from('realm_chat')
        .insert({
          realm_id: realmId,
          user_id: message.userId || null,
          user_name: message.userName || message.sender,
          text: message.text,
          created_at: new Date(message.timestamp || Date.now()).toISOString()
        });
      if (error) throw error;
    } catch (e) {
      console.warn('[DB] saveRealmChatMessage skipped:', e);
    }
  }

  async getWorldChatHistory(limit = 50): Promise<any[] | null> {
    try {
      const { data, error } = await this.supabase
        .from('world_chat')
        .select('*')
        .order('created_at', { ascending: true })
        .limit(limit);
      if (error) throw error;
      return (data || []).map((row: any) => ({
        id: row.id || `${row.created_at}`,
        sender: row.user_name || 'Unknown',
        userId: row.user_id,
        userName: row.user_name,
        text: row.text,
        timestamp: row.created_at
      }));
    } catch (e) {
      console.warn('[DB] getWorldChatHistory skipped:', e);
      return null;
    }
  }

  async getRealmChatHistory(realmId: string, limit = 50): Promise<any[] | null> {
    try {
      const { data, error } = await this.supabase
        .from('realm_chat')
        .select('*')
        .eq('realm_id', realmId)
        .order('created_at', { ascending: true })
        .limit(limit);
      if (error) throw error;
      return (data || []).map((row: any) => ({
        id: row.id || `${row.created_at}`,
        sender: row.user_name || 'Unknown',
        userId: row.user_id,
        userName: row.user_name,
        text: row.text,
        timestamp: row.created_at
      }));
    } catch (e) {
      console.warn('[DB] getRealmChatHistory skipped:', e);
      return null;
    }
  }

    // Expose Supabase client for services that need direct access
    getSupabase(): SupabaseClient {
        return this.supabase;
    }

    async createUserProfile(userId: string, email: string, displayName?: string) {
        const { data, error } = await this.supabase
            .from('user_profiles')
            .insert({ id: userId, email, display_name: displayName })
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async getUserProfile(userId: string): Promise<UserProfile | null> {
        const { data, error } = await this.supabase
            .from('user_profiles')
            .select('*')
            .eq('id', userId)
            .maybeSingle();
        if (error) throw error;
        return data ? this.mapToUserProfile(data) : null;
    }

    async updateUserProfile(userId: string, updates: Partial<UserProfile>) {
        const { data, error } = await this.supabase
            .from('user_profiles')
            .update(this.mapToDbUserProfile(updates))
            .eq('id', userId)
            .select();
        if (error) throw error;
        return data?.[0] || null;
    }

    async createMultiplayerRoom(roomData: any): Promise<MultiplayerRoom> {
        // Get current user to set as host
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) throw new Error('User must be authenticated to create a room');
        
        // Generate a 6-character invite code
        const generateInviteCode = () => {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let code = '';
            for (let i = 0; i < 6; i++) {
                code += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return code;
        };
        
        // Map camelCase to snake_case for database
        const dbData: any = {
            name: roomData.name,
            description: roomData.description,
            host_id: user.id, // Set the current user as the host
            max_players: roomData.maxPlayers || roomData.max_players,
            is_public: roomData.is_public !== undefined ? roomData.is_public : roomData.isPublic,
            current_players: [user.id], // Initialize with the host as the first player
            invite_code: generateInviteCode(),
            cartridge_id: roomData.cartridgeId || roomData.cartridge_id || null,
            is_active: roomData.is_active !== undefined ? roomData.is_active : true
        };
        
        const { data, error } = await this.supabase
            .from('multiplayer_rooms')
            .insert(dbData)
            .select()
            .single();
        if (error) throw error;
        return this.mapToMultiplayerRoom(data);
    }

    async getMultiplayerRooms(isPublic: boolean = true): Promise<MultiplayerRoom[]> {
        const { data, error } = await this.supabase
            .from('multiplayer_rooms')
            .select('*')
            .eq('is_public', isPublic)
            .order('updated_at', { ascending: false });
        if (error) throw error;
        return (data || []).map(this.mapToMultiplayerRoom);
    }

    async joinMultiplayerRoom(roomId: string, userId: string): Promise<MultiplayerRoom> {
        const { data, error } = await this.supabase.rpc('safe_add_player_to_room', { room_uuid: roomId, player_uuid: userId });
        if (error) throw error;
        const { data: roomData, error: fetchError } = await this.supabase.from('multiplayer_rooms').select('*').eq('id', roomId).single();
        if (fetchError) throw fetchError;
        return this.mapToMultiplayerRoom(roomData);
    }

    async leaveMultiplayerRoom(roomId: string, userId: string): Promise<void> {
        const { data: room, error: fetchError } = await this.supabase.from('multiplayer_rooms').select('current_players').eq('id', roomId).single();
        if (fetchError) throw fetchError;
        const currentPlayers = (room.current_players || []).filter((playerId: string) => playerId !== userId);
        const { error } = await this.supabase.from('multiplayer_rooms').update({ current_players: currentPlayers }).eq('id', roomId);
        if (error) throw error;
    }

    async createUserCartridge(cartridge: GameCartridge): Promise<GameCartridge> {
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) throw new Error('User must be authenticated to create a cartridge');
        
        const cartridgeData = {
            ...this.mapToDbGameCartridge(cartridge),
            author_id: user.id,
            author_name: cartridge.author || 'Unknown'
        };
        
        const { data, error } = await this.supabase
            .from('game_cartridges')
            .insert(cartridgeData)
            .select()
            .single();
            
        if (error) throw error;
        const createdCartridge = this.mapToGameCartridge(data);
        // Best-effort: generate music for new cartridge
        try {
            const { elevenLabsMusicService } = await import('./elevenlabsMusicService');
            await elevenLabsMusicService.ensurePlaylistForCartridge(createdCartridge, { numTracks: 1, duration: 60 });
        } catch (e) {
            console.warn('[Database] Music generation skipped:', e);
        }
        return createdCartridge;
    }

    // ... other database methods

    async createPlayerInteraction(interaction: any): Promise<void> {
        const { error } = await this.supabase.from('player_interactions').insert(interaction);
        if (error) throw error;
    }

    async getPlayerInteraction(interactionId: string): Promise<any | null> {
        const { data, error } = await this.supabase.from('player_interactions').select('*').eq('id', interactionId).single();
        if (error) throw error;
        return data;
    }

    async updatePlayerInteraction(interaction: any): Promise<void> {
        const { error } = await this.supabase.from('player_interactions').update(interaction).eq('id', interaction.id);
        if (error) throw error;
    }

    async saveUserProfile(profile: UserProfile): Promise<void> {
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) throw new Error('User must be authenticated');
        await this.updateUserProfile(user.id, profile);
    }

    async joinRoomByCode(inviteCode: string, userProfile: UserProfile): Promise<MultiplayerRoom | null> {
        try {
            console.log('[Database] Attempting to join room with invite code:', inviteCode);
            
            // Find room by invite code
            const { data: roomData, error: roomError } = await this.supabase
                .from('multiplayer_rooms')
                .select('*')
                .eq('invite_code', inviteCode)
                .eq('is_active', true)
                .single();

            if (roomError || !roomData) {
                console.log('[Database] Room not found with invite code:', inviteCode);
                return null;
            }

            // Join the room
            const room = await this.joinMultiplayerRoom(roomData.id, userProfile.uid || '');
            return room;
        } catch (error) {
            console.error('[Database] Error joining room by code:', error);
            return null;
        }
    }

    async saveGameState(gameState: {
        userProfile: any;
        currentPlayerState: any;
        currentStorySegment: any;
        selectedCartridge: any;
        chatMessages: any[];
        sessionId?: string;
        isMultiplayer: boolean;
        roomId?: string;
    }): Promise<string> {
        try {
            const { data: { user }, error: userError } = await this.supabase.auth.getUser();
            if (userError || !user) {
                throw new Error('Must be authenticated to save game state');
            }

            const sessionId = gameState.sessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const sessionData = {
                id: sessionId,
                user_id: user.id,
                cartridge_id: gameState.selectedCartridge?.id || null,
                room_id: gameState.roomId || null,
                is_multiplayer: gameState.isMultiplayer,
                game_state: {
                    // Core game state
                    userProfile: gameState.userProfile,
                    currentPlayerState: gameState.currentPlayerState,
                    currentStorySegment: gameState.currentStorySegment,
                    selectedCartridge: gameState.selectedCartridge,
                    chatMessages: gameState.chatMessages,
                    // Git-based story tracking (CRITICAL for story continuity)
                    currentBranchId: gameState.currentBranchId,
                    worldKarma: gameState.worldKarma,
                    karmaHistory: gameState.karmaHistory,
                    // Year Zero Engine state
                    yearZeroCharacter: gameState.yearZeroCharacter,
                    currentStress: gameState.currentStress,
                    activeMutations: gameState.activeMutations,
                    // Additional gameplay state
                    activeAgent: gameState.activeAgent,
                    currentSessionId: gameState.currentSessionId,
                    currentInstanceId: gameState.currentInstanceId,
                    // Screen state for proper restoration
                    currentScreen: gameState.currentScreen
                },
                updated_at: new Date().toISOString()
            };

            const { error } = await this.supabase
                .from('game_sessions')
                .upsert(sessionData);

            if (error) {
                console.error('[Database] Error saving game state:', error);
                throw error;
            }

            console.log('[Database] Game state saved successfully:', sessionId);
            return sessionId;
        } catch (error) {
            console.error('[Database] Failed to save game state:', error);
            throw error;
        }
    }

    async loadGameState(sessionId: string): Promise<any> {
        try {
            const { data: { user }, error: userError } = await this.supabase.auth.getUser();
            if (userError || !user) {
                throw new Error('Must be authenticated to load game state');
            }

            const { data, error } = await this.supabase
                .from('game_sessions')
                .select('*')
                .eq('id', sessionId)
                .eq('user_id', user.id)
                .maybeSingle();

            if (error) {
                console.error('[Database] Error loading game state:', error);
                throw error;
            }

            if (!data) {
                console.log('[Database] No game state found for session:', sessionId);
                return null;
            }

            console.log('[Database] Game state loaded successfully:', sessionId);
            return data.game_state;
        } catch (error) {
            console.error('[Database] Failed to load game state:', error);
            throw error;
        }
    }

    async createShareableGameLink(sessionId: string): Promise<string> {
        try {
            const { data: { user }, error: userError } = await this.supabase.auth.getUser();
            if (userError || !user) {
                throw new Error('Must be authenticated to create shareable link');
            }

            // Update the game session to be shareable
            const { error } = await this.supabase
                .from('game_sessions')
                .update({
                    is_shareable: true,
                    share_code: sessionId.substring(0, 8).toUpperCase(),
                    updated_at: new Date().toISOString()
                })
                .eq('id', sessionId)
                .eq('user_id', user.id);

            if (error) {
                console.error('[Database] Error making game shareable:', error);
                throw error;
            }

            // Return the shareable link
            const baseUrl = window.location.origin;
            const shareLink = `${baseUrl}?share=${sessionId}`;
            console.log('[Database] Shareable link created:', shareLink);
            return shareLink;
        } catch (error) {
            console.error('[Database] Failed to create shareable link:', error);
            throw error;
        }
    }

    // Alias for GameMenu compatibility
    async createGameInviteLink(sessionId: string): Promise<string> {
        return this.createShareableGameLink(sessionId);
    }

    async loadSharedGame(shareId: string): Promise<any> {
        try {
            const { data, error } = await this.supabase
                .from('game_sessions')
                .select('*')
                .eq('id', shareId)
                .eq('is_shareable', true)
                .maybeSingle();

            if (error) {
                console.error('[Database] Error loading shared game:', error);
                throw error;
            }

            if (!data) {
                console.log('[Database] No shared game found for ID:', shareId);
                return null;
            }

            console.log('[Database] Shared game loaded successfully:', shareId);
            return data.game_state;
        } catch (error) {
            console.error('[Database] Failed to load shared game:', error);
            throw error;
        }
    }

    private mapToUserProfile(data: any): UserProfile {
        return {
            uid: data.id,
            email: data.email,
            name: data.display_name || data.name,
            displayName: data.display_name || data.name,
            age: data.age,
            appearanceImageUrl: data.appearance_image_url,
            traits: data.traits || [],
            mindset: data.mindset || null,
            stats: data.stats || { health: { current: 100, max: 100 }, spiritualEnergy: { current: 50, max: 50 }, experience: 0, luck: 0, karma: 0 },
            skills: data.skills || [],
            inventory: data.inventory || [],
            creditsBalance: data.credits_balance || 100,
            subscriptionTier: data.subscription_tier || 'free'
        };
    }

    private mapToDbUserProfile(profile: Partial<UserProfile>): any {
        const dbProfile: any = {};
        if (profile.displayName !== undefined) dbProfile.display_name = profile.displayName;
        if (profile.age !== undefined) dbProfile.age = profile.age;
        if (profile.traits !== undefined) dbProfile.traits = profile.traits;
        if (profile.mindset !== undefined) dbProfile.mindset = profile.mindset;
        if (profile.appearanceImageUrl !== undefined) dbProfile.appearance_image_url = profile.appearanceImageUrl;
        if (profile.stats !== undefined) dbProfile.stats = profile.stats;
        if (profile.skills !== undefined) dbProfile.skills = profile.skills;
        if (profile.inventory !== undefined) dbProfile.inventory = profile.inventory;
        if (profile.creditsBalance !== undefined) dbProfile.credits_balance = profile.creditsBalance;
        if (profile.subscriptionTier !== undefined) dbProfile.subscription_tier = profile.subscriptionTier;
        return dbProfile;
    }

    private mapToGameCartridge(data: any): GameCartridge {
        return {
            id: data.id,
            name: data.name,
            description: data.description,
            version: data.version,
            author: data.author,
            authorId: data.author_id,
            thumbnailUrl: data.thumbnail_url,
            tags: data.tags || [],
            archetypes: data.archetypes || [],
            companions: data.companions || [],
            worldSettings: data.world_settings || {},
            storyPrompts: data.story_prompts || {},
            isBuiltIn: data.is_built_in,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            isPublic: data.is_public,
            maxPlayers: data.max_players,
            supportedFeatures: data.supported_features || []
        };
    }

    private mapToDbGameCartridge(cartridge: Partial<GameCartridge>): any {
        const dbCartridge: any = {};
        if (cartridge.name !== undefined) dbCartridge.name = cartridge.name;
        if (cartridge.description !== undefined) dbCartridge.description = cartridge.description;
        if (cartridge.version !== undefined) dbCartridge.version = cartridge.version;
        if (cartridge.author !== undefined) dbCartridge.author = cartridge.author;
        if (cartridge.authorId !== undefined) dbCartridge.author_id = cartridge.authorId;
        if (cartridge.thumbnailUrl !== undefined) dbCartridge.thumbnail_url = cartridge.thumbnailUrl;
        if (cartridge.tags !== undefined) dbCartridge.tags = cartridge.tags;
        if (cartridge.archetypes !== undefined) dbCartridge.archetypes = cartridge.archetypes;
        if (cartridge.companions !== undefined) dbCartridge.companions = cartridge.companions;
        if (cartridge.worldSettings !== undefined) dbCartridge.world_settings = cartridge.worldSettings;
        if (cartridge.storyPrompts !== undefined) dbCartridge.story_prompts = cartridge.storyPrompts;
        if (cartridge.isBuiltIn !== undefined) dbCartridge.is_built_in = cartridge.isBuiltIn;
        if (cartridge.isPublic !== undefined) dbCartridge.is_public = cartridge.isPublic;
        if (cartridge.maxPlayers !== undefined) dbCartridge.max_players = cartridge.maxPlayers;
        if (cartridge.supportedFeatures !== undefined) dbCartridge.supported_features = cartridge.supportedFeatures;
        return dbCartridge;
    }

    private mapToMultiplayerRoom(data: any): MultiplayerRoom {
        return {
            id: data.id,
            name: data.name,
            description: data.description,
            hostId: data.host_id,
            currentPlayers: data.current_players || [],
            maxPlayers: data.max_players,
            isPublic: data.is_public,
            gameState: data.game_state || {},
            cartridgeId: data.cartridge_id,
            inviteCode: data.invite_code,
            createdAt: new Date(data.created_at),
            updatedAt: new Date(data.updated_at)
        };
    }

    // Additional methods needed by components

    async getUserGameSessions(): Promise<any[]> {
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) throw new Error('User must be authenticated');
        
        const { data, error } = await this.supabase
            .from('game_sessions')
            .select('*')
            .eq('user_id', user.id)
            .order('updated_at', { ascending: false });
        
        if (error) throw error;
        return data || [];
    }

    subscribeToFeedUpdates(callback: (activity: any) => void): any {
        return this.supabase
            .channel('feed-updates')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'feed_posts' }, callback)
            .subscribe();
    }

    unsubscribeFromChannel(channel: any): void {
        this.supabase.removeChannel(channel);
    }

    async getUserActivityFeed(userId: string, limit: number = 20, offset: number = 0): Promise<any[]> {
        const { data, error } = await this.supabase
            .from('activity_feed')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .range(offset, offset + limit - 1);
        
        if (error) throw error;
        return data || [];
    }

    async getActivityFeed(limit: number = 20, offset: number = 0): Promise<any[]> {
        const { data, error } = await this.supabase
            .from('activity_feed')
            .select('*')
            .order('created_at', { ascending: false })
            .range(offset, offset + limit - 1);
        
        if (error) throw error;
        return data || [];
    }

    async createCharacterCreationPost(profile: UserProfile, imageUrl?: string): Promise<void> {
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) throw new Error('User must be authenticated');
        
        // Get user profile to get display name
        const userProfile = await this.getUserProfile(user.id);
        const userName = userProfile?.displayName || userProfile?.name || 'Unknown Player';
        
        const { error } = await this.supabase
            .from('feed_posts')  // Changed from 'activity_feed' to 'feed_posts'
            .insert({
                user_id: user.id,
                user_name: userName,  // Added required field
                post_type: 'character_created',  // Changed from 'character_creation' to match DB constraint
                title: `${profile.name} has entered the cyber realm`,
                description: `A new ${profile.traits?.join(', ') || 'mysterious'} character joins the cyber realm`,  // Added required field
                content: {
                    profile,
                    imageUrl
                },
                image_url: imageUrl || null,
                metadata: {
                    traits: profile.traits || [],
                    mindset: profile.mindset || null,
                    stats: profile.stats || {}
                }
            });
        
        if (error) throw error;
    }

    async createOrUpdateStoryProgressPost(
        sessionId: string,
        cartridgeId: string,
        cartridgeName: string,
        storySegmentCount: number,
        imageUrl: string,
        choicesMade: number
    ): Promise<void> {
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) throw new Error('User must be authenticated');
        
        // Get user profile to get display name
        const userProfile = await this.getUserProfile(user.id);
        const userName = userProfile?.displayName || userProfile?.name || 'Unknown Player';
        
        const { error } = await this.supabase
            .from('feed_posts')  // Changed from 'activity_feed' to 'feed_posts'
            .insert({
                user_id: user.id,
                user_name: userName,  // Added required field
                game_session_id: sessionId,
                post_type: 'story_progress',  // This matches the DB constraint
                title: `Progress in ${cartridgeName}`,
                description: `Chapter ${storySegmentCount} completed with ${choicesMade} choices made`,  // Added required field
                content: {
                    cartridgeId,
                    storySegmentCount,
                    imageUrl,
                    choicesMade
                },
                image_url: imageUrl || null,
                cartridge_id: cartridgeId,
                cartridge_name: cartridgeName,
                metadata: {
                    storySegmentCount,
                    choicesMade
                }
            });
        
        if (error) throw error;
    }

    async getNPCProfile(npcId: string): Promise<any> {
        const { data, error } = await this.supabase
            .from('npc_profiles')
            .select('*')
            .eq('id', npcId)
            .single();
        
        if (error) throw error;
        return data;
    }

    async getGameProfile(userId: string): Promise<any> {
        const { data, error } = await this.supabase
            .from('game_profiles')
            .select('*')
            .eq('user_id', userId)
            .single();
        
        if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "no rows returned"
        return data;
    }

    async createSocialInteraction(fromUserId: string, toUserId: string, type: string, data: any): Promise<boolean> {
        const { error } = await this.supabase
            .from('social_interactions')
            .insert({
                from_user_id: fromUserId,
                to_user_id: toUserId,
                interaction_type: type,
                interaction_data: data
            });
        
        if (error) {
            console.error('Failed to create social interaction:', error);
            return false;
        }
        return true;
    }

    async joinRoomByInviteCode(inviteCode: string): Promise<MultiplayerRoom> {
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) throw new Error('User must be authenticated');
        
        // First, find the room by invite code
        const { data: roomData, error: findError } = await this.supabase
            .from('multiplayer_rooms')
            .select('*')
            .eq('invite_code', inviteCode)
            .single();
        
        if (findError) throw new Error('Invalid room code');
        
        // Then join the room using the existing method
        return this.joinMultiplayerRoom(roomData.id, user.id);
    }

    async logApiUsage(service: string, model: string, inputTokens: number, outputTokens: number): Promise<void> {
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) return; // Skip logging if no user
        
        const { error } = await this.supabase
            .from('api_usage')
            .insert({
                user_id: user.id,
                service: service || 'unknown',
                service_name: service || 'unknown',
                model: model || 'unknown',
                model_name: model || 'unknown',
                input_tokens: inputTokens || 0,
                output_tokens: outputTokens || 0,
                total_tokens: (inputTokens || 0) + (outputTokens || 0),
                tokens_used: (inputTokens || 0) + (outputTokens || 0),
                request_count: 1
            });
        
        if (error) console.warn('Failed to log API usage:', error);
    }

    async clearImageCache(userId: string): Promise<void> {
        const { error } = await this.supabase
            .from('image_cache')
            .delete()
            .eq('user_id', userId);
        
        if (error) console.warn('Failed to clear image cache:', error);
    }

  async getUserCreatedCartridges(): Promise<any[]> {
        // Fetch all public cartridges plus user's own cartridges
        const { data: { user } } = await this.supabase.auth.getUser();
        
        let query = this.supabase
            .from('game_cartridges')
            .select('*');
        
    if (user) {
            // Get public cartridges OR cartridges created by the current user
            query = query.or(`is_public.eq.true,author_id.eq.${user.id}`);
        } else {
            // For non-authenticated users, only show public cartridges
            query = query.eq('is_public', true);
        }
        
        const { data, error } = await query.order('created_at', { ascending: false });
        
        if (error) {
            console.warn('Failed to get cartridges:', error);
            return [];
        }
        
    return data || [];
  }

  // ===== Bot activity persistence =====
  async saveBotState(state: { id: string; realmId: string; lastActionAt: number; engagedUntil?: number; cycles: number; alive: boolean; karma: number; }): Promise<void> {
    try {
      const payload = {
        bot_id: state.id,
        realm_id: state.realmId,
        last_action_at: new Date(state.lastActionAt).toISOString(),
        engaged_until: state.engagedUntil ? new Date(state.engagedUntil).toISOString() : null,
        cycles: state.cycles,
        alive: state.alive,
        karma: state.karma,
        updated_at: new Date().toISOString(),
      };
      // Upsert by (bot_id, realm_id)
      const { error } = await this.supabase.from('bot_activity').upsert(payload, { onConflict: 'bot_id,realm_id' });
      if (error) throw error;
    } catch (e) {
      console.warn('[DB] saveBotState skipped:', e);
    }
  }

  async getBotState(botId: string, realmId: string): Promise<any | null> {
    try {
      const { data, error } = await this.supabase
        .from('bot_activity')
        .select('*')
        .eq('bot_id', botId)
        .eq('realm_id', realmId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.bot_id,
        realmId: data.realm_id,
        lastActionAt: data.last_action_at ? new Date(data.last_action_at).getTime() : 0,
        engagedUntil: data.engaged_until ? new Date(data.engaged_until).getTime() : undefined,
        cycles: data.cycles || 0,
        alive: !!data.alive,
        karma: data.karma || 0,
      };
    } catch (e) {
      console.warn('[DB] getBotState skipped:', e);
      return null;
    }
  }

    async createStoryRepository(repository: any): Promise<void> {
        const { error } = await this.supabase
            .from('story_repositories')
            .insert(repository);
        
        if (error) throw error;
    }

    async createStoryVersion(version: any): Promise<void> {
        const { error } = await this.supabase
            .from('story_versions')
            .insert(version);
        
        if (error) throw error;
    }

    // Narrative Git-like branching system methods
    async getNarrativeBranch(branchName: string): Promise<any | null> {
        const { data, error } = await this.supabase
            .from('narrative_branches')
            .select('*')
            .eq('branch_name', branchName)
            .maybeSingle();
        
        if (error) {
            console.error('Error fetching narrative branch:', error);
            throw error;
        }
        
        return data;
    }

    async insertNarrativeBranch(branch: any): Promise<void> {
        const { error } = await this.supabase
            .from('narrative_branches')
            .insert({
                branch_name: branch.branchName,
                player_id: branch.playerId,
                hero_id: branch.heroId,
                instance_id: branch.instanceId,
                cartridge_id: branch.cartridgeId,
                last_commit_hash: branch.lastCommitHash,
                is_active: branch.isActive,
                commits: branch.commits || []
            });
        
        if (error) {
            console.error('Error inserting narrative branch:', error);
            throw error;
        }
    }

    async updateNarrativeBranch(branchName: string, updates: any): Promise<void> {
        const { error } = await this.supabase
            .from('narrative_branches')
            .update(updates)
            .eq('branch_name', branchName);
        
        if (error) {
            console.error('Error updating narrative branch:', error);
            throw error;
        }
    }

    async insertNarrativeCommit(commit: any): Promise<void> {
        // For now, commits are stored in the JSONB commits array within the branch
        // In a future enhancement, we might want a separate commits table
        const branchName = `/${commit.cartridgeId}/${commit.instanceId}/${commit.playerId}`;
        const branch = await this.getNarrativeBranch(branchName);
        
        if (branch) {
            const updatedCommits = [...(branch.commits || []), commit];
            await this.updateNarrativeBranch(branchName, {
                commits: updatedCommits,
                last_commit_hash: commit.hash
            });
        } else {
            console.error('Branch not found for commit:', branchName);
            throw new Error('Branch not found for commit');
        }
    }

    async insertInstanceCommit(commit: any, instanceMainBranch: string): Promise<void> {
        // Instance commits go to the instance's main branch
        const branch = await this.getNarrativeBranch(instanceMainBranch);
        
        if (branch) {
            const updatedCommits = [...(branch.commits || []), commit];
            await this.updateNarrativeBranch(instanceMainBranch, {
                commits: updatedCommits,
                last_commit_hash: commit.hash
            });
        } else {
            // Create the instance main branch if it doesn't exist
            await this.insertNarrativeBranch({
                branchName: instanceMainBranch,
                playerId: null, // System branch
                heroId: null,
                instanceId: instanceMainBranch.split('/')[2],
                cartridgeId: instanceMainBranch.split('/')[1],
                commits: [commit],
                lastCommitHash: commit.hash,
                isActive: true
            });
        }
    }

    async insertGlobalEvent(event: any): Promise<void> {
        // Global events could be stored in a separate table or as special commits
        // For now, we'll store them in a special universe branch
        const universeBranch = '/main';
        const branch = await this.getNarrativeBranch(universeBranch);
        
        if (branch) {
            const updatedCommits = [...(branch.commits || []), { type: 'global_event', ...event }];
            await this.updateNarrativeBranch(universeBranch, {
                commits: updatedCommits
            });
        } else {
            await this.insertNarrativeBranch({
                branchName: universeBranch,
                playerId: null,
                heroId: null,
                instanceId: null,
                cartridgeId: null,
                commits: [{ type: 'global_event', ...event }],
                lastCommitHash: event.id,
                isActive: true
            });
        }
    }

    async updateUniverseState(universeState: any): Promise<void> {
        // Store universe state in a special metadata branch
        const metadataBranch = '/universe_metadata';
        const branch = await this.getNarrativeBranch(metadataBranch);
        
        if (branch) {
            await this.updateNarrativeBranch(metadataBranch, {
                commits: [{ type: 'universe_state', ...universeState, timestamp: new Date().toISOString() }]
            });
        } else {
            await this.insertNarrativeBranch({
                branchName: metadataBranch,
                playerId: null,
                heroId: null,
                instanceId: null,
                cartridgeId: null,
                commits: [{ type: 'universe_state', ...universeState, timestamp: new Date().toISOString() }],
                lastCommitHash: 'universe_state_' + Date.now(),
                isActive: true
            });
        }
    }

    async insertChampionAvatar(champion: any): Promise<void> {
        // Champions could be stored in a dedicated table or as special branches
        const championBranch = `/champions/${champion.avatarId}`;
        
        await this.insertNarrativeBranch({
            branchName: championBranch,
            playerId: champion.originalPlayerId,
            heroId: champion.avatarId,
            instanceId: champion.originInstance,
            cartridgeId: champion.originCartridge,
            commits: [{ type: 'champion_creation', ...champion, timestamp: new Date().toISOString() }],
            lastCommitHash: 'champion_' + champion.avatarId,
            isActive: true
        });
    }
}

export const databaseService = new DatabaseService();
